import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Users, Pencil, Trash2, ChevronRight, Link2Off,
} from "lucide-react";
import { Patients, fetchPage, cursorOf, mergeById } from "@/lib/api";
import LoadMoreButton from "@/components/LoadMoreButton";
import NewPatientDocsStep from "@/components/NewPatientDocsStep";
import { useAuth } from "@/lib/auth";

const PAGE = 25;
import { useDraft, readDraft, clearDraft } from "@/lib/draft";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Disclaimer from "@/components/Disclaimer";

const EMPTY = { name: "", age: "", gender: "", occupation: "", email: "", phone: "", notes: "", clinical_history: "" };

export default function PatientsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [docsFor, setDocsFor] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [baseJson, setBaseJson] = useState(JSON.stringify(EMPTY));
  const draftKey = `patient-form:${editing?.id || "new"}`;
  useDraft(draftKey, dialogOpen ? form : null, JSON.stringify(form) !== baseJson);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const reqId = useRef(0);
  const load = async () => {
    const my = ++reqId.current;
    try {
      const { items, more } = await fetchPage(Patients.list, PAGE, null, { q: q.trim() || undefined });
      if (my !== reqId.current) return;
      setList(items); setHasMore(more);
    }
    catch { toast.error("Falha ao carregar pacientes"); }
    finally { if (my === reqId.current) setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const loadMore = async () => {
    const my = reqId.current;
    setLoadingMore(true);
    try {
      const { items, more } = await fetchPage(Patients.list, PAGE, cursorOf(list[list.length - 1]), { q: q.trim() || undefined });
      if (my !== reqId.current) return;
      setList(prev => mergeById(prev, items)); setHasMore(more);
    } catch { toast.error("Falha ao carregar mais pacientes"); }
    finally { setLoadingMore(false); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(p =>
      (p.name || "").toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s) ||
      (p.occupation || "").toLowerCase().includes(s),
    );
  }, [list, q]);

  const openNew = () => {
    setEditing(null); setBaseJson(JSON.stringify(EMPTY));
    setForm(readDraft("patient-form:new") || EMPTY); setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    const base = {
      name: p.name || "", age: String(p.age ?? ""), gender: p.gender || "",
      occupation: p.occupation || "", email: p.email || "", phone: p.phone || "",
      notes: p.notes || "", clinical_history: p.clinical_history || "",
    };
    setBaseJson(JSON.stringify(base));
    setForm(readDraft(`patient-form:${p.id}`) || base);
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name || (!editing && !form.age)) { toast.error("Nome e idade são obrigatórios"); return; }
    if (!form.gender) { toast.error("Seleciona o género — campo obrigatório."); return; }
    setBusy(true);
    try {
      const payload = { ...form, age: form.age === "" ? null : Number(form.age) };
      if (editing) {
        const up = await Patients.update(editing.id, payload);
        clearDraft(draftKey);
        setBaseJson(JSON.stringify(form));
        setList(prev => prev.map(x => x.id === up.id ? { ...x, ...up } : x));
        toast.success("Paciente atualizado");
        setDialogOpen(false);
      } else {
        const p = await Patients.create(payload);
        clearDraft(draftKey);
        setBaseJson(JSON.stringify(form));
        toast.success("Paciente criado");
        setDialogOpen(false);
        if (user?.mio_sign_enabled) setDocsFor(p); else nav(`/patients/${p.id}/intake`);
      }
    } catch {
      toast.error("Não foi possível guardar");
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminar "${p.name}" e toda a sua informação?`)) return;
    try {
      await Patients.remove(p.id);
      setList(prev => prev.filter(x => x.id !== p.id));
      toast.success("Paciente eliminado");
    } catch { toast.error("Não foi possível eliminar"); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="patients-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Gestão</div>
          <h1 className="font-display text-4xl text-slate-50 mt-2 flex items-center gap-3">
            <Users className="w-7 h-7 text-sky-400" strokeWidth={1.5} /> Pacientes
          </h1>
          <p className="text-slate-400 text-sm mt-1">Lista completa · criar, editar, eliminar e abrir a ficha de cada paciente.</p>
        </div>
        <Button data-testid="new-patient-btn" onClick={openNew} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full glow-sky">
          <Plus className="w-4 h-4 mr-2" /> Novo paciente
        </Button>
      </header>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="patients-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Pesquisar por nome, email ou ocupação…"
          className="pl-9 bg-slate-950 border-slate-800 text-slate-100 placeholder:text-slate-500"
        />
      </div>

      {loading ? (
        <div className="text-slate-500">A carregar…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center bg-slate-900/30">
          <Users className="w-10 h-10 mx-auto text-slate-600" strokeWidth={1.5} />
          <div className="mt-3 text-slate-300">{list.length === 0 && !q.trim() ? "Ainda não há pacientes" : "Sem resultados para esta pesquisa."}</div>
          {list.length === 0 && !q.trim() && (
            <div className="text-slate-500 text-sm mt-1">Cria o primeiro paciente para começar.</div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden divide-y divide-slate-800 fade-in-stagger" data-testid="patients-list">
          {filtered.map(p => (
            <div key={p.id} data-testid={`patient-row-${p.id}`} className="flex items-center gap-2 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 hover:bg-slate-900/80 transition min-w-0">
              <Link to={`/patients/${p.id}`} className="flex-1 min-w-0 flex items-center gap-4">
                <div className="w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-200 font-medium">
                  {(p.name || "??").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-50 font-medium truncate">{p.name}</span>
                    {p.patient_active === false && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-300 uppercase tracking-wider">
                        <Link2Off className="w-3 h-3" /> Inativo
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {p.age != null ? `${p.age} anos` : "Idade não registada"} · {p.gender || "—"}
                    {p.occupation ? ` · ${p.occupation}` : ""}
                    {p.email ? ` · ${p.email}` : ""}
                  </div>
                </div>
              </Link>
              <button
                data-testid={`edit-patient-${p.id}`}
                onClick={() => openEdit(p)}
                title="Editar"
                className="p-2 rounded-full text-slate-400 hover:text-sky-300 hover:bg-slate-800"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                data-testid={`delete-patient-${p.id}`}
                onClick={() => remove(p)}
                title="Eliminar"
                className="p-2 rounded-full text-slate-400 hover:text-rose-300 hover:bg-slate-800"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <Link
                to={`/patients/${p.id}`}
                data-testid={`open-patient-${p.id}`}
                className="p-2 rounded-full text-slate-500 hover:text-slate-100 hover:bg-slate-800"
                title="Abrir ficha"
              >
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      )}
      {!loading && hasMore && <LoadMoreButton testId="patients-load-more" onClick={loadMore} loading={loadingMore} />}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100" data-testid="patient-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-50 font-display">{editing ? "Editar paciente" : "Novo paciente"}</DialogTitle>
            <DialogDescription className="text-slate-400">Dados base — podes completar a anamnese depois.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-slate-300">Nome *</Label>
                <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Idade *</Label>
                <Input data-testid="form-age" type="number" min="0" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Género *</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="form-gender" className="bg-slate-900 border-slate-800 text-slate-50 mt-1">
                    <SelectValue placeholder="Seleciona" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-50">
                    <SelectItem value="masculino">Masculino</SelectItem>
                    <SelectItem value="feminino">Feminino</SelectItem>
                    <SelectItem value="neutro">Neutro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Ocupação</Label>
                <Input data-testid="form-occupation" value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input data-testid="form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Telemóvel (E.164)</Label>
                <Input data-testid="form-phone" type="tel" placeholder="+351912345678" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1 font-mono text-xs" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Notas</Label>
              <Textarea data-testid="form-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Historial clínico</Label>
              <Textarea data-testid="form-history" rows={3} value={form.clinical_history} onChange={(e) => setForm({ ...form, clinical_history: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button data-testid="save-patient-btn" onClick={submit} disabled={busy} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6">
              {busy ? "A guardar…" : (editing ? "Guardar" : "Criar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {docsFor && <NewPatientDocsStep patient={docsFor} onDone={() => { const pid = docsFor.id; setDocsFor(null); nav(`/patients/${pid}/intake`); }} />}
    </div>
  );
}
